import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldLabel,
  getFieldName,
  getFieldText,
  getFieldValue,
  getLetterheadLogoDataURL,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FieldTable,
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
} from "../common/ControlBundle";
import FileUpload from "../common/FileUpload";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import {
  getAttachments,
  getCareModalities,
  getFosterBeds,
  html2PdfWithStyle,
  sendEmailMessage,
} from "../services/soar";
import { getSchema } from "./joi/validation";
import cryptoRandomString from "crypto-random-string";

function SCTFCIRv3() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadStatus, setLoadStatus] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, isDataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadStatus.loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            isDataSet: true,
          };
        });

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    loadStatus.loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = useCallback(
    async (
      dataOverride,
      formsyDetailsOverride,
      commentsOverride,
      signaturesOverride,
      autoSave = false
    ) => {
      if (isSaveInProgress) {
        return;
      }

      const dataObj = dataOverride || data;
      const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
      const commentsObj =
        commentsOverride === undefined
          ? undefined
          : commentsOverride || comments;
      const signaturesObj = signaturesOverride || signatures;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setTouched({});
      const errs = { ...errors };

      if (!dataObj.SelectedProgram?.["@value"]) {
        errs.SelectedProgram = '"Selected Program" is required';
        setErrors(errs);
        return;
      }

      delete errs.SelectedProgram;
      setErrors(errs);
      setIsSaveInProgress(true);

      try {
        const submissionResponse = await saveForm(
          dataObj,
          formsyDetailsObj,
          commentsObj,
          signaturesObj,
          navigate,
          location,
          autoSave
        );

        if (!isMountedRef.current || !submissionResponse?.Id) {
          return;
        }

        const newDetails = { ...formsyDetailsObj };
        newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
        newDetails.ModifiedById = safyInfo.user.azureId;
        newDetails.ModifiedByName = safyInfo.user.displayName;

        if (newDetails.SubmissionId === "") {
          newDetails.SubmissionId = submissionResponse?.Id;
        }

        setFormsyDetails(newDetails);
        return submissionResponse;
      } finally {
        setIsSaveInProgress(false);
      }
    },
    [
      data,
      formsyDetails,
      comments,
      signatures,
      errors,
      isSaveInProgress,
      location,
      navigate,
      safyInfo,
      timeoutId,
    ]
  );

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  // const checkFinalSigned = (sigArray) => {
  //   const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

  //   const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
  //   console.log("requiredSignatureTypes: ", requiredSignatureTypes);
  //   console.log("capturedSignatureTypes: ", capturedSignatureTypes);

  //   const missingSigTypes = requiredSignatureTypes.filter(
  //     (type) => !capturedSignatureTypes.includes(type)
  //   );

  //   return missingSigTypes.length === 0;
  // };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [careModalities, setCareModalities] = useState();
  const [newAmendment, setNewAmendment] = useState("");
  const [fileAttachments, setFileAttachments] = useState([]);

  const modules = useMemo(() => {
    return safyInfo?.user?.modules || [];
  }, [safyInfo]);

  const resolutionAmendmentsMemo = useMemo(() => {
    if (
      !getFieldValue(data.ResolutionAmendments) ||
      !Array.isArray(JSON.parse(getFieldValue(data.ResolutionAmendments)))
    ) {
      return [];
    }

    return JSON.parse(getFieldValue(data.ResolutionAmendments));
  }, [data]);

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const isFosterHomeSet = useMemo(() => {
    if (!data.FosterHome) {
      return undefined;
    }

    const value = getFieldValue(data.FosterHome);
    return value !== undefined && value !== "" ? true : false;
  }, [data.FosterHome]);

  useEffect(() => {
    if (!loadStatus.isDataSet || isFosterHomeSet) {
      return;
    }

    let ignore = false;
    const loadFosterBeds = async () => {
      const beds = await getFosterBeds(clientid);

      if (!ignore) {
        const activeBeds = beds.filter(
          (bed) => bed.ccx_actualdischarge === undefined
        );

        if (activeBeds.length > 0) {
          const newestActiveBed = activeBeds.sort((a, b) => {
            const aDate = moment(getFieldDate(a.ccx_actualadmission));
            const bDate = moment(getFieldDate(b.ccx_actualadmission));

            if (aDate.isBefore(bDate)) return 1;
            if (bDate.isBefore(aDate)) return -1;
            return 0;
          })[0];
          const name = getFieldName(newestActiveBed.ccx_fostercarehome);
          const id = getFieldText(newestActiveBed.ccx_fostercarehome);
          setData((d) => {
            const obj = { ...d };
            obj.FosterHome["@value"] = name;
            obj.FosterHome["@id"] = id;
            return obj;
          });
          setFormsyDetails((fd) => {
            const obj = { ...fd };
            obj.TertiaryObjectId = id;
            obj.TertiaryObjectName = name;
            return obj;
          });
        }
      }
    };

    loadFosterBeds();

    return () => {
      ignore = true;
    };
  }, [clientid, loadStatus.isDataSet, isFosterHomeSet]);

  const processLevelOfCare = () => {
    setData((d) => {
      console.group("processLevelOfCare");
      const obj = { ...d };
      console.log("IncidentDate", getFieldValue(obj.IncidentDate));
      if (!getFieldValue(obj.IncidentDate)) {
        return obj;
      }

      const selectedProgram = clientData.programs.filter(
        (option) =>
          option.ccx_client_programid === getFieldValue(obj.SelectedProgram)
      )[0];

      if (!selectedProgram) {
        return obj;
      }

      console.log("selectedProgram", selectedProgram.ccx_name);

      if (careModalities?.length > 0) {
        const incidentDate = moment(getFieldValue(obj.IncidentDate));
        const modalityForIR = careModalities.filter((modality) => {
          if (
            !modality.ccx_clientprogramid["@name"].includes(
              selectedProgram.ccx_name.replace(" - TFC", "")
            )
          ) {
            return false;
          }

          const start = moment(getFieldDate(modality.ccx_startdate));
          const end = moment(getFieldDate(modality.ccx_enddate));

          if (start.isAfter(incidentDate) || end.isBefore(incidentDate)) {
            return false;
          }

          return true;
        })[0];

        if (modalityForIR?.ccx_caremodality) {
          console.log("modalityForIR", modalityForIR.ccx_caremodality);
          obj.LOC["@value"] = getFieldName(modalityForIR.ccx_caremodality);
        }
      } else {
        delete obj.LOC["@value"];
      }

      console.groupEnd();
      return obj;
    });
  };

  useEffect(() => {
    const loadCareModalities = async () => {
      const response = await getCareModalities(clientid);

      if (!isMountedRef.current) {
        return;
      }

      if (!response?.sort) {
        setCareModalities([]);
        return;
      }

      const sortedModalities = response.sort((a, b) => {
        const aStart = moment(getFieldDate(a.ccx_startdate));
        const bStart = moment(getFieldDate(b.ccx_startdate));

        if (aStart.isAfter(bStart)) return 1;
        if (bStart.isAfter(aStart)) return -1;
        return 0;
      });

      setCareModalities(sortedModalities);
    };

    loadCareModalities();
  }, [clientid]);

  const getIRType = () => {
    const types = [];

    if (getFieldValue(data.ClientDeath) === "true") {
      types.push(data.ClientDeath["@label"]);
    }
    if (getFieldValue(data.SuicideAttempt) === "true") {
      types.push(data.SuicideAttempt["@label"]);
    }
    if (getFieldValue(data.Emergency) === "true") {
      types.push(data.Emergency["@label"]);
    }
    if (getFieldValue(data.AWOL) === "true") {
      types.push(data.AWOL["@label"]);
    }
    if (getFieldValue(data.SeriousInjury) === "true") {
      types.push(data.SeriousInjury["@label"]);
    }
    if (getFieldValue(data.SuicideGesture) === "true") {
      types.push(data.SuicideGesture["@label"]);
    }
    if (getFieldValue(data.MedicationError) === "true") {
      types.push(data.MedicationError["@label"]);
    }
    if (getFieldValue(data.OffsiteTreatment) === "true") {
      types.push(data.OffsiteTreatment["@label"]);
    }
    if (getFieldValue(data.OffsiteAssessment) === "true") {
      types.push(data.OffsiteAssessment["@label"]);
    }
    if (getFieldValue(data.Weapon) === "true") {
      types.push(data.Weapon["@label"]);
    }
    if (getFieldValue(data.Substance) === "true") {
      types.push(data.Substance["@label"]);
    }
    if (getFieldValue(data.SchoolRemoval) === "true") {
      types.push(data.SchoolRemoval["@label"]);
    }
    if (getFieldValue(data.OutsideAgency) === "true") {
      types.push(data.OutsideAgency["@label"]);
    }
    if (getFieldValue(data.Restrained) === "true") {
      types.push(data.Restrained["@label"]);
    }
    if (getFieldValue(data.PlacementChange) === "true") {
      types.push(data.PlacementChange["@label"]);
    }
    if (getFieldValue(data.SexTrafficking) === "true") {
      types.push(data.SexTrafficking["@label"]);
    }

    return types.join(", ");
  };

  const getMainEmailBody = (selectedProgram) => {
    const siteUrl =
      process.env.REACT_APP_ENVIRONMENT === "Production"
        ? "safyportal.safy.org"
        : "safyportaltest.safy.org";

    return `
<p>
  Link: https://${siteUrl}/share/${
      JSON.parse(formsyConfig.SetupConfiguration[0]).formPath
    }v3/${formsyDetails.FormVersionId}/${formsyDetails.ContactId}/${
      formsyDetails.SubmissionId
    }
</p>
<p>
  <b>Type of Incident:</b> ${getIRType()}
</p>
<p>
  <b>Client Name:</b> ${getFieldName(selectedProgram.ccx_patient)}
  <br/>
  <b>Date of birth:</b> ${getFieldDate(clientData.client.dob)}
</p>
<p>
  <b>Name of Program:</b> ${getFieldName(selectedProgram.ccx_programunit)}${
      getFieldValue(data.FosterHome)
        ? `
    <br/>
    <b>Foster Home:</b> ${getFieldValue(data.FosterHome)}`
        : null
    }
</p>
<p>
  <b>Incident Resolved:</b> ${getFieldValue(data.ResolvedStatus)}
</p>
`;
  };

  const getIncidentDescription = () => {
    return `Describe the incident and the circumstances surrounding it: ${getFieldValue(
      data.IncidentDescription
    )}`;
  };

  const addAmendment = async () => {
    let amendments = [];

    if (getFieldValue(data.ResolutionAmendments)) {
      const resolutionAmendments = JSON.parse(
        getFieldValue(data.ResolutionAmendments)
      );
      amendments = Array.isArray(resolutionAmendments)
        ? resolutionAmendments
        : [];
    }

    const amendment = {
      _id: cryptoRandomString(10),
      CreatedBy: safyInfo.user.displayName,
      CreatedOn: new Date(),
      Amendment: newAmendment,
    };
    amendments.push(amendment);

    setData((d) => {
      const obj = {
        ...d,
        ResolutionAmendments: formConfig.ResolutionAmendments,
      };
      obj.ResolutionAmendments["@value"] = JSON.stringify(amendments);
      handleSubmit(obj);
      return obj;
    });

    setNewAmendment("");

    const selectedProgram = clientData.programs.filter(
      (option) =>
        option.ccx_client_programid === getFieldValue(data.SelectedProgram)
    )[0];

    if (!selectedProgram?.["unit.sor_irconfig"]) {
      return;
    }

    let config;

    try {
      config = JSON.parse(selectedProgram["unit.sor_irconfig"]);
    } catch (e) {
      if (e.name === "SyntaxError") {
        toast.error("Error parsing Program Unit's IR Config");
      } else {
        console.log(e.message);
      }
    }

    if (!config?.Amended) {
      return;
    }
    const printStyle = `body {
      font-family: "Times New Roman";
      font-size: .75rem;
    }`;

    const pdf = await html2PdfWithStyle(pdfRef.current, printStyle);
    let attachments = [
      {
        filebytes: pdf,
        filename: `${formsyConfig.FormDescription}.pdf`,
        filetype: "application/pdf",
      },
    ];

    if (fileAttachments.length > 0) {
      attachments = attachments.concat(
        fileAttachments
          .filter((attachment) => attachment.FileType === "application/pdf")
          .map((attachment) => {
            return {
              filebytes: attachment.FileContent.split(",")[1],
              filename: attachment.FileName,
              filetype: attachment.FileType,
            };
          })
      );
    }

    let recipientList = [...config.Amended];

    if (getFieldValue(data.SuicideAttempt) === "true") {
      recipientList = recipientList.concat(config.SuicideAttempt);
    }

    sendEmailMessage(
      recipientList,
      undefined,
      `Incident Report Amended - ${getIRType()}`,
      `${getMainEmailBody(selectedProgram)}
${getIncidentDescription()}
<p>Amended by: ${safyInfo.user.displayName}, ${safyInfo.user.domainName}</p>`,
      undefined,
      attachments
    );
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    let isClinicalReviewer = false;
    let obj = { ...data };

    if (getFieldValue(obj.CompletedBy) === "true") {
      obj.CompletedBy["@value"] = "false";
      obj.CompletedBy["@id"] = safyInfo.user.azureId;
      obj.CompletedBy["@name"] = safyInfo.user.displayName;
      obj.CompletedBy["@credentials"] =
        signatureData[signatureData.length - 1].name;
      obj.CompletedBy["@title"] =
        signatureData[signatureData.length - 1].signatureRole;
      obj.CompletedBy["@date"] =
        signatureData[signatureData.length - 1].datetime;
    }

    if (getFieldValue(obj.ClinicalReviewer) === "true") {
      isClinicalReviewer = true;
      obj.ClinicalReviewer["@value"] = "false";
      obj.ClinicalReviewer["@id"] = safyInfo.user.azureId;
      obj.ClinicalReviewer["@name"] = safyInfo.user.displayName;
      obj.ClinicalReviewer["@credentials"] =
        signatureData[signatureData.length - 1].name;
      obj.ClinicalReviewer["@title"] =
        signatureData[signatureData.length - 1].signatureRole;
      obj.ClinicalReviewer["@date"] =
        signatureData[signatureData.length - 1].datetime;
    }

    if (getFieldValue(obj.AdministrativeReviewer) === "true") {
      obj.AdministrativeReviewer["@value"] = "false";
      obj.AdministrativeReviewer["@id"] = safyInfo.user.azureId;
      obj.AdministrativeReviewer["@name"] = safyInfo.user.displayName;
      obj.AdministrativeReviewer["@credentials"] =
        signatureData[signatureData.length - 1].name;
      obj.AdministrativeReviewer["@title"] =
        signatureData[signatureData.length - 1].signatureRole;
      obj.AdministrativeReviewer["@date"] =
        signatureData[signatureData.length - 1].datetime;
    }

    setData(obj);

    if (formsyDetailsObj.FinalSigned !== "1" && isClinicalReviewer) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);

      const selectedProgram = clientData.programs.find(
        (option) =>
          option.ccx_client_programid === getFieldValue(obj.SelectedProgram)
      );

      const response = await handleSubmit(
        obj,
        formsyDetailsObj,
        formsyDetailsObj.FinalSigned === "1" ? undefined : null,
        sigArray
      );

      if (response?.Id && selectedProgram?.["unit.sor_irconfig"]) {
        let config;

        try {
          config = JSON.parse(selectedProgram["unit.sor_irconfig"]);
        } catch (e) {
          if (e.name === "SyntaxError") {
            toast.error("Error parsing Program Unit's IR Config");
          } else {
            console.log(e.message);
          }
        }

        if (config?.Signed) {
          const sendFinalSignedNotification = async () => {
            const printStyle = `body {
                font-family: "Times New Roman";
                font-size: .75rem;
              }`;

            const pdf = await html2PdfWithStyle(pdfRef.current, printStyle);
            let attachments = [
              {
                filebytes: pdf,
                filename: `${formsyConfig.FormDescription}.pdf`,
                filetype: "application/pdf",
              },
            ];

            if (fileAttachments.length > 0) {
              attachments = attachments.concat(
                fileAttachments
                  .filter(
                    (attachment) => attachment.FileType === "application/pdf"
                  )
                  .map((attachment) => {
                    return {
                      filebytes: attachment.FileContent.split(",")[1],
                      filename: attachment.FileName,
                      filetype: attachment.FileType,
                    };
                  })
              );
            }

            const highPriority =
              getFieldValue(obj.ClientDeath) === "true" ||
              getFieldValue(obj.SuicideAttempt) === "true" ||
              getFieldValue(obj.SeriousInjury) === "true";
            let recipientList = [...config.Signed];

            if (getFieldValue(obj.SuicideAttempt) === "true") {
              recipientList = recipientList.concat(config.SuicideAttempt);
            }

            sendEmailMessage(
              recipientList,
              undefined,
              `${
                highPriority ? `\u2757 ` : ""
              }Incident Report Final Signed - ${getIRType()}`,
              `${getMainEmailBody(selectedProgram)}
            ${getIncidentDescription()}
            <p><b>Name/Title of Person Completing Form</b>: ${getFieldName(
              obj.CompletedBy
            )}, ${obj.CompletedBy["@title"]}</p>
            <p><b>Name of Clinician Reviewing Form</b>: ${getFieldName(
              obj.ClinicalReviewer
            )}</p>`,
              undefined,
              attachments,
              highPriority
            );
          };

          sendFinalSignedNotification();
        }
      }
    } else {
      handleSubmit(
        obj,
        formsyDetailsObj,
        formsyDetailsObj.FinalSigned === "1" ? undefined : null,
        sigArray
      );
    }
  };

  const customValidate = useCallback(
    (errs) => {
      setErrors(errs);

      let signatureTypeSelected =
        getFieldValue(data.CompletedBy) === "true" ||
        getFieldValue(data.ClinicalReviewer) === "true" ||
        getFieldValue(data.AdministrativeReviewer) === "true";

      if (!signatureTypeSelected) {
        if (errs)
          errs.SignatureType = "At least one Signature Type is required.";
        else
          errs = {
            SignatureType: "At least one Signature Type is required.",
          };
      } else {
        delete errs.SignatureType;
      }

      return errs;
    },
    [data]
  );

  const handleIncidentDateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });
    processLevelOfCare();
  };

  const getOutcomeField = useCallback(
    (selectedType) => {
      if (!formsyConfig.FormConfiguration) {
        return;
      }

      const selectedOption = JSON.parse(
        formsyConfig.FormConfiguration[0].config[0].SAFYTypeOfIncident[
          "@options"
        ].replaceAll("'", '"')
      ).filter((t) => t._id === selectedType)[0];

      if (!selectedOption) {
        console.log("no selected type match for: ", selectedType);
        return;
      }

      return selectedOption.outcomeField;
    },
    [formsyConfig]
  );

  const getOutcomeOptions = useCallback(
    (selectedType) => {
      const outcomeField = getOutcomeField(selectedType);

      if (!outcomeField) {
        return [];
      }

      const outcomeOptions = JSON.parse(
        formsyConfig.FormConfiguration[0].config[0].SAFYOutcome[
          `@${outcomeField}Options`
        ].replaceAll("'", '"')
      );
      return outcomeOptions || [];
    },
    [formsyConfig, getOutcomeField]
  );

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint={true}
          letterhead={
            <img
              src={getLetterheadLogoDataURL()}
              alt="safy logo"
              className="me-4"
              style={{ width: "200px", height: "auto" }}
            />
          }
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`body {
            font-family: "Times New Roman";
            font-size: .75rem;
          }`}
          printReport={
            loadStatus.loadingConfig === false && qs.printPdf === null
          }
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="container border-top border-start">
                <div className="row border-bottom pt-1 text-center">
                  <div className="h4 fw-bold">
                    {formsyConfig.FormDescription}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-7 border-end py-2">
                    <b>{data.SelectedProgram["@label"]}</b>{" "}
                    {
                      clientData.programs?.filter(
                        (program) =>
                          program.ccx_client_programid ===
                          data.SelectedProgram["@value"]
                      )[0]?.ccx_programunit["@name"]
                    }
                  </div>
                  <div className="col border-end py-2">
                    <b>{data.LOC["@label"]}</b> {data.LOC["@value"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-7 border-end py-2">
                    <b>{data.ClientNameLabel["@label"]}</b>{" "}
                    {clientData.client?.fullName}
                  </div>
                  <div className="col border-end py-2">
                    <b>{data.DOBLabel["@label"]}</b>{" "}
                    {clientData.client?.dob["@date"]}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-7 border-end py-2">
                    <div className="d-print-none">
                      <FormInput
                        name="IncidentDate"
                        label={data.IncidentDate["@label"]}
                        value={getFieldValue(data.IncidentDate)}
                        onChange={handleIncidentDateChange}
                        identical={compare("IncidentDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="date"
                        labelClassName="fw-bold"
                        divClassName="d-flex"
                        className="ms-2 flex-fill"
                        max={moment().format(moment.HTML5_FMT.DATE)}
                      />
                      {errors.IncidentDate !== undefined ? (
                        <div className="alert alert-danger">
                          {errors.IncidentDate}
                        </div>
                      ) : null}
                    </div>
                    <div className="d-none d-print-inline">
                      <b>{data.IncidentDate["@label"]}</b>{" "}
                      {moment(getFieldValue(data.IncidentDate)).format("L")}
                    </div>
                  </div>
                  <div className="col border-end py-2">
                    <div className="d-print-none">
                      <FormInput
                        name="IncidentTime"
                        label={data.IncidentTime["@label"]}
                        value={getFieldValue(data.IncidentTime)}
                        onChange={handleChange}
                        identical={compare("IncidentTime")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        type="time"
                        labelClassName="fw-bold"
                        divClassName="d-flex"
                        className="ms-2 flex-fill"
                      />
                      {errors.IncidentTime !== undefined ? (
                        <div className="alert alert-danger">
                          {errors.IncidentTime}
                        </div>
                      ) : null}
                    </div>
                    <div className="d-none d-print-inline">
                      <b>{data.IncidentTime["@label"]}</b>{" "}
                      {moment(getFieldValue(data.IncidentTime), "HHmm").format(
                        "hh:mm A"
                      )}
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <b>{data.FosterHome["@label"]}</b> {data.FosterHome["@sub"]}{" "}
                    {getFieldValue(data.FosterHome)}
                  </div>
                </div>
                <FieldTable
                  name="StaffDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row fw-bold">
                    <div className="col-7 border-end py-2">
                      {data.StaffDetails["@col1"]}
                    </div>
                    <div className="col border-end py-2">
                      {data.StaffDetails["@col2"]}
                    </div>
                  </div>
                  {data.StaffDetails["@value"]
                    ? JSON.parse(data.StaffDetails["@value"]).map(
                        (item, index) => (
                          <React.Fragment key={`StaffDetails.${item._id}`}>
                            <div className="row border-bottom">
                              <div className="col-7 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "StaffDetails");
                                      }}
                                    ></i>
                                    {index > 2 ? (
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "StaffDetails"
                                          );
                                        }}
                                      ></i>
                                    ) : null}
                                    {index + 1}.
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "StaffDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      className="ms-2"
                                      divClassName="flex-fill"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "StaffDetails");
                                      }}
                                    ></i>
                                    {index + 1}.{" "}
                                    {moment.isDate(item.Name)
                                      ? moment(item.Name).format("M/D/YYYY")
                                      : item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <div className="d-flex">
                                    <div>{index + 1}.</div>
                                    <FormInput
                                      name="Position"
                                      value={item.Position}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "StaffDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      className="ms-2"
                                      divClassName="flex-fill"
                                    />
                                  </div>
                                ) : (
                                  <>
                                    {index + 1}. {item.Position}
                                  </>
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        )
                      )
                    : null}
                  {!formDisabled ? (
                    <div className="row border-bottom d-print-none">
                      <div className="col border-end py-2">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            handleAddRow("StaffDetails", { IsEditable: true });
                          }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {errors.StaffDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.StaffDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <div className="h5">{data.TypeOfIncident["@label"]}</div>
                    {errors.TypeOfIncident ? (
                      <div className="alert alert-danger">
                        {errors.TypeOfIncident}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <div className="fst-italic text-decoration-underline fw-bold">
                      {data.TypeOfIncident["@sub1"]}
                    </div>
                    <FormCheckbox
                      name="ClientDeath"
                      label={data.ClientDeath["@label"]}
                      checked={getFieldValue(data.ClientDeath) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="SuicideAttempt"
                      label={data.SuicideAttempt["@label"]}
                      checked={getFieldValue(data.SuicideAttempt) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="Emergency"
                      label={data.Emergency["@label"]}
                      checked={getFieldValue(data.Emergency) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormRadioButton
                      name="EmergencyType"
                      label={data.EmergencyType["@label"]}
                      error={errors.EmergencyType}
                      value={getFieldValue(data.EmergencyType)}
                      options={JSON.parse(
                        data.EmergencyType["@options"].replaceAll("'", '"')
                      )}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || getFieldValue(data.Emergency) !== "true"
                      }
                      className="ms-4"
                    />
                    <FormCheckbox
                      name="AWOL"
                      label={data.AWOL["@label"]}
                      checked={getFieldValue(data.AWOL) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormInput
                      name="AWOLReturnDate"
                      label={data.AWOLReturnDate["@label"]}
                      error={errors.AWOLReturnDate}
                      value={getFieldValue(data.AWOLReturnDate)}
                      onChange={handleChange}
                      identical={compare("AWOLReturnDate")}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled || getFieldValue(data.AWOL) !== "true"
                      }
                      type="datetime"
                      divClassName="ms-5"
                    />
                  </div>
                  <div className="col border-end py-2">
                    <div className="fst-italic text-decoration-underline fw-bold">
                      {data.TypeOfIncident["@sub2"]}
                    </div>
                    <FormCheckbox
                      name="SeriousInjury"
                      label={data.SeriousInjury["@label"]}
                      checked={getFieldValue(data.SeriousInjury) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="SuicideGesture"
                      label={data.SuicideGesture["@label"]}
                      checked={getFieldValue(data.SuicideGesture) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="MedicationError"
                      label={data.MedicationError["@label"]}
                      checked={getFieldValue(data.MedicationError) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="OffsiteTreatment"
                      label={data.OffsiteTreatment["@label"]}
                      checked={getFieldValue(data.OffsiteTreatment) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <div className="ms-5 d-flex">
                      <div className="me-2 fst-italic">
                        {data.OffsiteEmergencyTreatmentLocation["@label"]}
                      </div>
                      <div className="flex-fill">
                        <div className="w-100">
                          <FormInput
                            name="OffsiteEmergencyTreatmentLocation"
                            error={errors.OffsiteEmergencyTreatmentLocation}
                            value={getFieldValue(
                              data.OffsiteEmergencyTreatmentLocation
                            )}
                            onChange={handleChange}
                            identical={compare(
                              "OffsiteEmergencyTreatmentLocation"
                            )}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              getFieldValue(data.OffsiteTreatment) !== "true"
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <FormCheckbox
                      name="OffsiteAssessment"
                      label={data.OffsiteAssessment["@label"]}
                      checked={getFieldValue(data.OffsiteAssessment) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <div className="ms-5 d-flex">
                      <div className="me-2 fst-italic">
                        {data.OffsiteEmergencyAssessmentLocation["@label"]}
                      </div>
                      <div className="flex-fill">
                        <div className="w-100">
                          <FormInput
                            name="OffsiteEmergencyAssessmentLocation"
                            error={errors.OffsiteEmergencyAssessmentLocation}
                            value={getFieldValue(
                              data.OffsiteEmergencyAssessmentLocation
                            )}
                            onChange={handleChange}
                            identical={compare(
                              "OffsiteEmergencyAssessmentLocation"
                            )}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              getFieldValue(data.OffsiteAssessment) !== "true"
                            }
                            divClassName="ms-4"
                          />
                        </div>
                      </div>
                    </div>
                    <FormCheckbox
                      name="Weapon"
                      label={data.Weapon["@label"]}
                      checked={getFieldValue(data.Weapon) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <div className="ms-5 d-flex">
                      <div className="me-2 fst-italic">
                        {data.WeaponType["@label"]}
                      </div>
                      <div className="flex-fill">
                        <div className="w-100">
                          <FormInput
                            name="WeaponType"
                            error={errors.WeaponType}
                            value={getFieldValue(data.WeaponType)}
                            onChange={handleChange}
                            identical={compare("WeaponType")}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              getFieldValue(data.Weapon) !== "true"
                            }
                            divClassName="ms-4"
                          />
                        </div>
                      </div>
                    </div>
                    <FormCheckbox
                      name="Substance"
                      label={data.Substance["@label"]}
                      checked={getFieldValue(data.Substance) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <div className="ms-5 d-flex">
                      <div className="me-2 fst-italic">
                        {data.SubstanceType["@label"]}
                      </div>
                      <div className="flex-fill">
                        <div className="w-100">
                          <FormInput
                            name="SubstanceType"
                            error={errors.SubstanceType}
                            value={getFieldValue(data.SubstanceType)}
                            onChange={handleChange}
                            identical={compare("SubstanceType")}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              getFieldValue(data.Substance) !== "true"
                            }
                            divClassName="ms-4"
                          />
                        </div>
                      </div>
                    </div>
                    <FormCheckbox
                      name="SchoolRemoval"
                      label={data.SchoolRemoval["@label"]}
                      checked={getFieldValue(data.SchoolRemoval) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormRadioButton
                      name="SchoolRemovalType"
                      label={data.SchoolRemovalType["@label"]}
                      error={errors.SchoolRemovalType}
                      value={getFieldValue(data.SchoolRemovalType)}
                      options={JSON.parse(
                        data.SchoolRemovalType["@options"].replaceAll("'", '"')
                      )}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        getFieldValue(data.SchoolRemoval) !== "true"
                      }
                      divClassName="ms-4"
                    />
                    <FormCheckbox
                      name="OutsideAgency"
                      label={data.OutsideAgency["@label"]}
                      checked={getFieldValue(data.OutsideAgency) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <div className="ms-5 d-flex">
                      <div className="me-2 fst-italic">
                        {data.AgencyInvolved["@label"]}
                      </div>
                      <div className="flex-fill">
                        <div className="w-100">
                          <FormInput
                            name="AgencyInvolved"
                            error={errors.AgencyInvolved}
                            value={getFieldValue(data.AgencyInvolved)}
                            onChange={handleChange}
                            identical={compare("AgencyInvolved")}
                            commentsHandler={handleComments}
                            disabled={
                              formDisabled ||
                              getFieldValue(data.OutsideAgency) !== "true"
                            }
                            divClassName="ms-4"
                          />
                        </div>
                      </div>
                    </div>
                    <FormCheckbox
                      name="Restrained"
                      label={data.Restrained["@label"]}
                      checked={getFieldValue(data.Restrained) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormCheckbox
                      name="PlacementChange"
                      label={data.PlacementChange["@label"]}
                      checked={getFieldValue(data.PlacementChange) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    <FormRadioButton
                      name="PlacementChangeType"
                      label={data.PlacementChangeType["@label"]}
                      error={errors.PlacementChangeType}
                      value={getFieldValue(data.PlacementChangeType)}
                      options={JSON.parse(
                        data.PlacementChangeType["@options"].replaceAll(
                          "'",
                          '"'
                        )
                      )}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={
                        formDisabled ||
                        getFieldValue(data.PlacementChange) !== "true"
                      }
                      divClassName="ms-4"
                    />
                    <FormCheckbox
                      name="SexTrafficking"
                      label={data.SexTrafficking["@label"]}
                      checked={getFieldValue(data.SexTrafficking) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="IncidentDescription"
                      label={data.IncidentDescription["@label"]}
                      error={errors.IncidentDescription}
                      value={getFieldValue(data.IncidentDescription)}
                      onChange={handleChange}
                      identical={compare("IncidentDescription")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="5"
                      labelClassName="h5"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="PrecipitatingFactors"
                      label={data.PrecipitatingFactors["@label"]}
                      error={errors.PrecipitatingFactors}
                      value={getFieldValue(data.PrecipitatingFactors)}
                      onChange={handleChange}
                      identical={compare("PrecipitatingFactors")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="3"
                      labelClassName="h5"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="Interventions"
                      label={data.Interventions["@label"]}
                      error={errors.Interventions}
                      value={getFieldValue(data.Interventions)}
                      onChange={handleChange}
                      identical={compare("Interventions")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="5"
                      labelClassName="h5"
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col border-end py-2">
                    <FormTextArea
                      name="FollowUpActions"
                      label={data.FollowUpActions["@label"]}
                      error={errors.FollowUpActions}
                      value={getFieldValue(data.FollowUpActions)}
                      onChange={handleChange}
                      identical={compare("FollowUpActions")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      rows="3"
                      labelClassName="h5"
                    />
                  </div>
                </div>
                <FieldTable
                  name="NotificationDetails"
                  commentsHandler={handleComments}
                >
                  <div className="row border-bottom">
                    <div className="col border-end py-2 h5">
                      {data.NotificationDetails["@label"]}
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col-2 border-end py-2 fw-bold text-center">
                      {data.NotificationDetails["@col1"]}
                    </div>
                    <div className="col-3 border-end py-2 fw-bold text-center">
                      {data.NotificationDetails["@col2"]}
                    </div>
                    <div className="col-2 border-end py-2 fw-bold text-center">
                      {data.NotificationDetails["@col3"]}
                    </div>
                    <div className="col-2 border-end py-2 fw-bold text-center">
                      {data.NotificationDetails["@col4"]}
                    </div>
                    <div className="col-3 border-end py-2 fw-bold text-center">
                      {data.NotificationDetails["@col5"]}
                    </div>
                  </div>
                  {data.NotificationDetails["@value"]
                    ? JSON.parse(data.NotificationDetails["@value"]).map(
                        (item, index) => (
                          <React.Fragment
                            key={`NotificationDetails.${item._id}`}
                          >
                            <div className="row border-bottom">
                              <div className="col-2 border-end py-2 d-flex">
                                {index <= 5 ? (
                                  JSON.parse(
                                    data.NotificationDetails[
                                      "@roleOptions"
                                    ].replaceAll("'", '"')
                                  )[index].name
                                ) : (
                                  <>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "NotificationDetails"
                                        );
                                      }}
                                    ></i>
                                    Other
                                  </>
                                )}
                              </div>
                              <div className="col-3 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Notified"
                                    value={item.Notified}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "NotificationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Notified
                                )}
                              </div>
                              <div className="col-2 border-end py-2 px-1">
                                <div className="d-print-none">
                                  <FormInput
                                    name="Date"
                                    value={item.Date}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "NotificationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                    type="date"
                                  />
                                </div>
                                <div className="d-none d-print-inline">
                                  {item.Date
                                    ? moment(item.Date).format("l")
                                    : null}
                                </div>
                              </div>
                              <div className="col-2 border-end py-2 px-1">
                                <div className="d-print-none">
                                  <FormInput
                                    name="Time"
                                    value={item.Time}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "NotificationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                    type="time"
                                  />
                                </div>
                                <div className="d-none d-print-inline">
                                  {item.Date && item.Time
                                    ? moment(
                                        `${item.Date}T${item.Time}`
                                      ).format("LT")
                                    : null}
                                </div>
                              </div>
                              <div className="col-3 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="NotifiedBy"
                                    value={item.NotifiedBy}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "NotificationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.NotifiedBy
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        )
                      )
                    : null}
                  {!formDisabled ? (
                    <div className="row border-bottom d-print-none">
                      <div className="col border-end py-2">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            handleAddRow("NotificationDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {errors.NotificationDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.NotificationDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <FormRadioButton
                  name="ResolvedStatus"
                  label={data.ResolvedStatus["@label"]}
                  error={errors.ResolvedStatus}
                  value={getFieldValue(data.ResolvedStatus)}
                  options={JSON.parse(
                    data.ResolvedStatus["@options"].replaceAll("'", '"')
                  )}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  divClassName="d-print-none"
                />
                <div className="row border-bottom d-print-none">
                  <div className="col border-end py-2">
                    {data.CompletedBy["@id"] ? (
                      <div className="ms-3">
                        {data.CompletedBy["@label"]} {data.CompletedBy["@name"]}
                      </div>
                    ) : (
                      <FormCheckbox
                        name="CompletedBy"
                        label={data.CompletedBy["@label"]}
                        checked={getFieldValue(data.CompletedBy) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled || data.CompletedBy["@id"]}
                        divClassName="ms-3"
                      />
                    )}
                    {data.ClinicalReviewer["@id"] ? (
                      <div className="ms-3">
                        {data.ClinicalReviewer["@label"]}{" "}
                        {data.ClinicalReviewer["@name"]}
                      </div>
                    ) : (
                      <FormCheckbox
                        name="ClinicalReviewer"
                        label={data.ClinicalReviewer["@label"]}
                        checked={
                          getFieldValue(data.ClinicalReviewer) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled || data.ClinicalReviewer["@id"]}
                        divClassName="ms-3"
                      />
                    )}
                    {data.AdministrativeReviewer["@id"] ? (
                      <div className="ms-3">
                        {data.AdministrativeReviewer["@label"]}{" "}
                        {data.AdministrativeReviewer["@name"]}
                      </div>
                    ) : (
                      <FormCheckbox
                        name="AdministrativeReviewer"
                        label={data.AdministrativeReviewer["@label"]}
                        checked={
                          getFieldValue(data.AdministrativeReviewer) === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={data.AdministrativeReviewer["@id"]}
                        divClassName="ms-3"
                      />
                    )}
                    {errors.SignatureType !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.SignatureType}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <span className="d-none d-print-inline">
                  <div className="row border-bottom">
                    <div className="col border-end p-2">
                      <div className="row">
                        <div className="col-8 fw-bold">
                          {data.CompletedBy["@label"]}
                        </div>
                        <div className="col d-flex">
                          <div className="fw-bold">
                            {data.CompletedBy["@label3"]}
                          </div>
                          <div className="flex-fill">
                            {data.CompletedBy["@date"]
                              ? moment(data.CompletedBy["@date"]).format("l")
                              : null}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col mx-3 border-bottom">
                          {data.CompletedBy["@name"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.CompletedBy["@credentials"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.CompletedBy["@title"]}
                        </div>
                      </div>
                      <div className="row fw-bold fst-italic">
                        <div className="col fw-bold fst-italic">
                          {data.CompletedBy["@subLabel1"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.CompletedBy["@subLabel2"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.CompletedBy["@subLabel3"]}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col border-end p-2">
                      <div className="row">
                        <div className="col-8 fw-bold">
                          {data.ClinicalReviewer["@label"]}
                        </div>
                        <div className="col d-flex">
                          <div className="fw-bold">
                            {data.ClinicalReviewer["@label3"]}
                          </div>
                          <div className="flex-fill">
                            {data.ClinicalReviewer["@date"]
                              ? moment(data.ClinicalReviewer["@date"]).format(
                                  "l"
                                )
                              : null}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col mx-3 border-bottom">
                          {data.ClinicalReviewer["@name"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.ClinicalReviewer["@credentials"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.ClinicalReviewer["@title"]}
                        </div>
                      </div>
                      <div className="row fw-bold fst-italic">
                        <div className="col fw-bold fst-italic">
                          {data.ClinicalReviewer["@subLabel1"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.ClinicalReviewer["@subLabel2"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.ClinicalReviewer["@subLabel3"]}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row border-bottom">
                    <div className="col border-end p-2">
                      <div className="row">
                        <div className="col-8 fw-bold">
                          {data.AdministrativeReviewer["@label"]}
                        </div>
                        <div className="col d-flex">
                          <div className="fw-bold">
                            {data.AdministrativeReviewer["@label3"]}
                          </div>
                          <div className="flex-fill">
                            {data.AdministrativeReviewer["@date"]
                              ? moment(
                                  data.AdministrativeReviewer["@date"]
                                ).format("l")
                              : null}
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col mx-3 border-bottom">
                          {data.AdministrativeReviewer["@name"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.AdministrativeReviewer["@credentials"]}
                        </div>
                        <div className="col mx-3 border-bottom">
                          {data.AdministrativeReviewer["@title"]}
                        </div>
                      </div>
                      <div className="row fw-bold fst-italic">
                        <div className="col fw-bold fst-italic">
                          {data.AdministrativeReviewer["@subLabel1"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.AdministrativeReviewer["@subLabel2"]}
                        </div>
                        <div className="col fw-bold fst-italic">
                          {data.AdministrativeReviewer["@subLabel3"]}
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
                <FieldTable name="CopyDetails" commentsHandler={handleComments}>
                  <div className="row border-bottom">
                    <div className="col border-end py-2">
                      <div className="h5">{data.CopyDetails["@label"]}</div>
                    </div>
                  </div>
                  <div className="row border-bottom fw-bold text-center">
                    <div className="col-4 border-end py-2">
                      {data.CopyDetails["@col1"]}
                    </div>
                    <div className="col-2 border-end py-2">
                      {data.CopyDetails["@col2"]}
                    </div>
                    <div className="col-2 border-end py-2">
                      {data.CopyDetails["@col3"]}
                    </div>
                    <div className="col-4 border-end py-2">
                      {data.CopyDetails["@col4"]}
                    </div>
                  </div>
                  {data.CopyDetails["@value"]
                    ? JSON.parse(data.CopyDetails["@value"]).map(
                        (item, index) => (
                          <React.Fragment key={`CopyDetails.${item._id}`}>
                            <div className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "CopyDetails");
                                      }}
                                    ></i>
                                    {index > 1 ? (
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(index, "CopyDetails");
                                        }}
                                      ></i>
                                    ) : null}
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CopyDetails"
                                        );
                                      }}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "CopyDetails");
                                      }}
                                    ></i>
                                    {item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col-2 border-end py-2">
                                <div className="d-print-none">
                                  <FormInput
                                    name="Date"
                                    value={item.Date}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "CopyDetails");
                                    }}
                                    type="date"
                                  />
                                </div>
                                <div className="d-none d-print-inline">
                                  {item.Date
                                    ? moment(item.Date).format("l")
                                    : null}
                                </div>
                              </div>
                              <div className="col-2 border-end py-2">
                                <div className="d-print-none">
                                  <FormInput
                                    name="Time"
                                    value={item.Time}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "CopyDetails");
                                    }}
                                    type="time"
                                  />
                                </div>
                                <div className="d-none d-print-inline">
                                  {item.Date && item.Time
                                    ? moment(
                                        `${item.Date}T${item.Time}`
                                      ).format("LT")
                                    : null}
                                </div>
                              </div>
                              <div className="col-4 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Method"
                                    value={item.Method}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "CopyDetails");
                                    }}
                                  />
                                ) : (
                                  item.Method
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        )
                      )
                    : null}
                  {errors.CopyDetails !== undefined ? (
                    <div className="row mt-2">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.CopyDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row border-bottom d-print-none">
                    <div className="col border-end mb-2">
                      <button
                        className="btn btn-primary mt-0"
                        onClick={() => {
                          handleAddRow("CopyDetails", { IsEditable: true });
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </FieldTable>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <div className="d-print-none">
              <Signature
                canvasWidth="450"
                canvasHeight="200"
                modalTitle="Report Signatures"
                careTeams={careTeams}
                signatures={signatures}
                onSign={saveSignature}
                clientData={clientData.client}
                showModal={showSignatureModal}
                onHide={() => {
                  setShowSignatureModal(false);
                }}
                finalSignType={helperConfiguration.finalSignType}
                initialSignatureType={helperConfiguration.signatureType}
                marginBottom="50px"
              />
              {formsyDetails.FinalSigned === "1" &&
              (modules.includes("COMQI") || modules.includes("Admin")) ? (
                <div
                  className="bg-mdwarning rounded p-2"
                  style={{ marginBottom: "100px" }}
                >
                  <div className="h6">
                    SAFY Metrics -{" "}
                    <span className="text-danger">(COMQI ONLY)</span>
                  </div>
                  <FormSelect
                    name="SAFYTypeOfIncident"
                    label={data.SAFYTypeOfIncident["@label"]}
                    value={getFieldValue(data.SAFYTypeOfIncident)}
                    options={JSON.parse(
                      data.SAFYTypeOfIncident["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                  />
                  {getFieldValue(data.SAFYTypeOfIncident) === "AWOL" ? (
                    <>
                      <FormRadioButton
                        name="SAFYAWOLLocated"
                        label={data.SAFYAWOLLocated["@label"]}
                        error={errors.SAFYAWOLLocated}
                        value={getFieldValue(data.SAFYAWOLLocated)}
                        options={JSON.parse(
                          data.SAFYAWOLLocated["@options"].replaceAll("'", '"')
                        )}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                      />
                      {getFieldValue(data.SAFYAWOLLocated) === "Yes" ? (
                        <FormSelect
                          name="SAFYOutcome"
                          label={data.SAFYOutcome["@label"]}
                          value={getFieldValue(data.SAFYOutcome)}
                          options={getOutcomeOptions(
                            getFieldValue(data.SAFYTypeOfIncident)
                          )}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                        />
                      ) : null}
                    </>
                  ) : getFieldValue(data.SAFYTypeOfIncident) ===
                    "Problematic Behaviors at School/Other Care Settings" ? (
                    <>
                      <div>
                        {
                          data.SAFYProblematicBehaviorsOutcomeDetention[
                            "@header"
                          ]
                        }
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeDetention"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeDetention[
                                "@label"
                              ]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeDetention
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeSuspension"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeSuspension[
                                "@label"
                              ]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeSuspension
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeExpulsion"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeExpulsion[
                                "@label"
                              ]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeExpulsion
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeSafetyPlan"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeSafetyPlan[
                                "@label"
                              ]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeSafetyPlan
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeIEP"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeIEP["@label"]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeIEP
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SAFYProblematicBehaviorsOutcomeOther"
                            label={
                              data.SAFYProblematicBehaviorsOutcomeOther[
                                "@label"
                              ]
                            }
                            checked={
                              getFieldValue(
                                data.SAFYProblematicBehaviorsOutcomeOther
                              ) === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            divClassName="ms-3"
                          />
                        </div>
                      </div>
                    </>
                  ) : getOutcomeField(
                      getFieldValue(data.SAFYTypeOfIncident)
                    ) ? (
                    <FormSelect
                      name="SAFYOutcome"
                      label={data.SAFYOutcome["@label"]}
                      value={getFieldValue(data.SAFYOutcome)}
                      options={getOutcomeOptions(
                        getFieldValue(data.SAFYTypeOfIncident)
                      )}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                    />
                  ) : null}
                </div>
              ) : null}
              {formsyDetails.FinalSigned === "1" ? (
                <>
                  <div className="border rounded my-4 p-2 bg-light">
                    <div className="h6">
                      {getFieldLabel(formConfig.ResolutionHeader)}
                    </div>
                    <div className="my-4">
                      {resolutionAmendmentsMemo.map((item) => (
                        <div className="mt-2 mb-4 mx-4" key={item._id}>
                          <div className="w-100">
                            <div
                              className={`mx-4 h6 ${
                                safyInfo.user.displayName === item.CreatedBy
                                  ? " text-end"
                                  : "text-start"
                              }`}
                            >
                              {`${item.CreatedBy} - ${new Date(
                                item.CreatedOn
                              ).toLocaleString()}`}
                            </div>
                          </div>
                          <div className="border rounded bg-white p-2">{`${item.Amendment}`}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mb-4">
                      <FormInput
                        name="NewAmendment"
                        label="New Resolution/Amendment:"
                        onChange={({ currentTarget: input }) => {
                          setNewAmendment(input.value);
                        }}
                        value={newAmendment}
                      />
                    </div>
                    <div className="row">
                      <div className="col">
                        <div
                          className="btn btn-success float-end me-4 mb-2"
                          onClick={() => addAmendment()}
                        >
                          Add
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            {submissionid ? (
              <FileUpload
                header={<div className="h5">Upload documentation here:</div>}
                objectId={submissionid}
                objectType="formsy"
                savedFiles={fileAttachments}
                printImg={true}
                loadFiles={loadFiles}
              />
            ) : null}
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCTFCIRv3;
